import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { pickGoogleDoc, preloadDrivePicker, isPopupBlocked, isDriveScopeDenied } from '../../utils/googleDrivePicker';
import { messageFromError } from '../../utils/apiError';   // Block 233
import { showErrorToast } from '../../utils/toast';        // Block 233
import { Button, Checkbox, Input, Textarea, Field, Banner, EmptyState, ErrorState, Skeleton, StatusPill, TabGroup, TabPanel, v } from '../../ui';
import '../MeetingMode/meeting.css';

// ── Shared: CRM update section ────────────────────────────────────────────────
function CRMUpdateSection({ crmSuggestions }) {
  const [checked, setChecked] = useState(() => new Set(crmSuggestions.map((_, i) => i)));
  const [updating, setUpdating] = useState(false);
  const [done, setDone] = useState(false);

  function toggle(i) {
    setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }

  async function handleUpdate() {
    setUpdating(true);
    for (const [i, s] of crmSuggestions.entries()) {
      if (!checked.has(i)) continue;
      try {
        // Block 219a: "I met this person" is an INTERACTION, not a direct write to
        // lastContactedAt. Setting the field through the PUT left no record behind
        // it — the contact looked recently-contacted with nothing to show for it,
        // and the nudge engine was suppressed on a value nothing could explain.
        // The PUT no longer accepts lastContactedAt; the interaction advances it.
        await api(`/api/contacts/${s.contactId}/interactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'meeting',
            direction: null,
            summary: s.suggestNote,
          }),
        });
        // The note append is a separate concern and stays on the PUT.
        await api(`/api/contacts/${s.contactId}`, {
          method: 'PUT',
          body: JSON.stringify({
            notes: s.existingNotes ? `${s.existingNotes}\n${s.suggestNote}` : s.suggestNote,
          }),
        });
      } catch (err) { showErrorToast(messageFromError(err, "Could not update.")); }
    }
    setUpdating(false);
    setDone(true);
  }

  if (done) {
    return (
      <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-green') }}>
        <p className="mtg-done">Contacts updated</p>
      </section>
    );
  }

  return (
    <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-orange') }} aria-label="Update contacts">
      <span className="mtg-eyebrow">Update contacts</span>
      {crmSuggestions.map((s, i) => (
        <Checkbox
          key={s.contactId}
          checked={checked.has(i)}
          onChange={() => toggle(i)}
          label={(
            <span className="mtg-stack" style={{ gap: 2 }}>
              <span>{s.contactName}</span>
              <span className="mtg-meta">Log this meeting and add a note: &ldquo;{s.suggestNote}&rdquo;</span>
            </span>
          )}
        />
      ))}
      <div className="mtg-actions">
        <Button variant="secondary" onClick={handleUpdate} disabled={updating || checked.size === 0} aria-busy={updating || undefined}>
          {updating ? 'Updating…' : `Update ${checked.size} contact${checked.size === 1 ? '' : 's'}`}
        </Button>
      </div>
    </section>
  );
}

// ── Shared: post-meeting result card ─────────────────────────────────────────
function PostMeetingCard({ result, onCreateTasks, onDismiss }) {
  const items = result.actionItems || [];
  const [checked, setChecked] = useState(() => new Set(items.map((_, i) => i)));
  const [editedItems, setEditedItems] = useState(items.map(x => ({ ...x })));
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const allChecked = checked.size === items.length;

  function toggleAll() { setChecked(allChecked ? new Set() : new Set(items.map((_, i) => i))); }
  function toggle(i) { setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; }); }
  function updateField(i, field, val) {
    setEditedItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: val } : item));
  }

  async function handleCreate() {
    setCreating(true);
    await onCreateTasks(editedItems.filter((_, i) => checked.has(i)));
    setCreated(true);
    setCreating(false);
  }

  const crmSuggestions = Array.isArray(result.crmSuggestions) ? result.crmSuggestions : [];

  return (
    <div className="mtg-stack">
      {result.noteTitle && (
        <p className="mtg-done">Saved to Notes: &ldquo;{result.noteTitle}&rdquo;</p>
      )}

      <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-blue') }}>
        <span className="mtg-eyebrow">Summary</span>
        <p className="mtg-text mtg-text--ink">{result.summary}</p>
      </section>

      {Array.isArray(result.decisions) && result.decisions.length > 0 && (
        <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-green') }}>
          <span className="mtg-eyebrow">Decisions</span>
          <ul className="mtg-list">
            {result.decisions.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </section>
      )}

      {items.length > 0 && (
        <section className="mtg-card">
          <div className="mtg-card__head">
            <span className="mtg-eyebrow">Action items</span>
            <button type="button" className="mtg-link ui-hit" onClick={toggleAll}>
              {allChecked ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="mtg-stack" style={{ gap: 'var(--space-2)' }}>
            {editedItems.map((item, i) => (
              <div key={i} className={`mtg-item ${checked.has(i) ? 'mtg-item--on' : 'mtg-item--off'}`}>
                <Checkbox
                  checked={checked.has(i)}
                  onChange={() => toggle(i)}
                  label={<span className="ui-visually-hidden">Create a task for: {item.task}</span>}
                />
                <div className="mtg-item__main">
                  <Input
                    value={item.task}
                    onChange={e => updateField(i, 'task', e.target.value)}
                    aria-label="Action item"
                  />
                  {(item.assignee || item.dueDate) && (
                    <p className="mtg-meta">
                      {item.assignee && <span>{item.assignee}</span>}
                      {item.dueDate && <span>by {item.dueDate}</span>}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {crmSuggestions.length > 0 && <CRMUpdateSection crmSuggestions={crmSuggestions} />}

      <div className="mtg-actions">
        {items.length > 0 && !created && (
          <Button variant="confirm" onClick={handleCreate} disabled={creating || checked.size === 0} aria-busy={creating || undefined}>
            {creating ? 'Creating…' : `Create ${checked.size} task${checked.size === 1 ? '' : 's'}`}
          </Button>
        )}
        {created && <p className="mtg-done" role="status">Tasks created</p>}
        <Button variant="tertiary" onClick={onDismiss}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ── Shared: recording card ─────────────────────────────────────────────────────
function RecordingCard({ recording, idKey, processingId, results, onProcess, onCreateTasks, onDismissResult }) {
  const id = recording[idKey];
  const result = results[id];
  const isProcessing = processingId === id;
  const hasResult = result && !result.error;

  return (
    <section className="mtg-card" data-testid="meeting-recording">
      <div className="mtg-card__head" style={{ alignItems: 'flex-start' }}>
        <div className="mtg-stack" style={{ gap: 'var(--space-1-5)', flex: '1 1 200px' }}>
          <h3 className="mtg-h mtg-h--sm">{recording.meetingTitle}</h3>
          <div className="mtg-meta">
            {recording.date && <span>{recording.date}</span>}
            {recording.duration && <span>{recording.duration} min</span>}
            <StatusPill tone="success">Transcript available</StatusPill>
          </div>
        </div>
        {!hasResult && (
          <Button
            variant="confirm"
            onClick={() => onProcess(recording)}
            disabled={isProcessing || !!processingId}
            aria-busy={isProcessing || undefined}
          >
            {isProcessing ? (
              <><span className="mtg-spin mtg-spin--sm" aria-hidden="true" />Processing…</>
            ) : 'Process with Aidey'}
          </Button>
        )}
        {hasResult && (
          <StatusPill tone="success">
            Processed{result._via === 'picker' ? ', via Google Drive' : ''}
          </StatusPill>
        )}
      </div>

      {result?.error && (
        <Banner tone="problem" action={<Button variant="secondary" size="sm" className="ui-hit" onClick={() => onProcess(recording)}>Retry</Button>}>
          Processing failed: {result.error}
        </Banner>
      )}

      {hasResult && (
        <PostMeetingCard
          result={result}
          onCreateTasks={(items) => onCreateTasks(recording, items)}
          onDismiss={() => onDismissResult(id)}
        />
      )}
    </section>
  );
}

// ── Platform panel hook ───────────────────────────────────────────────────────
function usePlatformPanel(endpoint, idKey) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [results, setResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api(endpoint);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Failed to load recordings');
        return;
      }
      const data = await res.json();
      setRecordings(data.recordings || []);
    } catch (err) {
      // api() throws on any non-2xx, so the server's own message arrives here (the !res.ok branch
      // above is unreachable). It used to be replaced by "Could not reach the server" whatever the
      // server said, which also hid the reconnect / transcript-permission hints the Teams tab reads.
      setError(messageFromError(err, 'Could not reach the server'));
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  // resolveTranscript (optional): async fn returning either the transcript text, or an object
  // { transcriptText, source } so the caller can record WHICH path produced it. Zoom/Teams pass no
  // resolver and use the text inline on the recording. Google Meet (Block 178) resolves at process
  // time: the Drive Picker is PRIMARY and reports source:'picker'; if it's cancelled/fails, the
  // resolver falls back to the Drive-search transcript and reports source:'drive-search'. `source`
  // is stored on the result as `_via` so the UI can show which path actually ran. A null/empty
  // return means nothing to process (e.g. a standalone pick cancelled) → abort quietly.
  async function process(recording, processEndpoint, resolveTranscript) {
    const id = recording[idKey];
    setProcessingId(id);
    try {
      let transcriptText, via;
      if (resolveTranscript) {
        const r = await resolveTranscript(recording);
        if (!r) { setProcessingId(null); return; }               // cancelled / nothing → degrade, no error
        if (typeof r === 'string') transcriptText = r;
        else { transcriptText = r.transcriptText; via = r.source; }
      } else {
        transcriptText = recording.transcriptText;
      }
      if (!transcriptText) { setProcessingId(null); return; }
      const res = await api(processEndpoint, {
        method: 'POST',
        body: JSON.stringify({
          [idKey]: id,
          transcriptText,
          meetingTitle: recording.meetingTitle,
          attendees: recording.attendees || [],
        }),
      });
      if (!res.ok) throw new Error('Processing failed');
      const data = await res.json();
      setResults(prev => ({ ...prev, [id]: via ? { ...data, _via: via } : data }));
      window.dispatchEvent(new Event('aidey:notesUpdated'));
    } catch (err) {
      setResults(prev => ({ ...prev, [id]: { error: err.message } }));
    } finally {
      setProcessingId(null);
    }
  }

  function dismissResult(id) {
    setResults(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  // Add a recording to the top of the list if it isn't already there (used when a user picks a
  // transcript straight from Drive that wasn't among the resolved candidates — Route 3 standalone).
  function upsertRecording(rec) {
    setRecordings(prev => prev.some(r => r[idKey] === rec[idKey]) ? prev : [rec, ...prev]);
  }

  return { loading, error, recordings, processingId, results, load, process, dismissResult, upsertRecording };
}

// ── Shared: platform tab body ─────────────────────────────────────────────────
function PlatformTabBody({ loading, error, recordings, processingId, results, idKey, emptyNote, emptyHint, connectNote, onLoad, onProcess, onCreateTasks, onDismissResult, onPickFromDrive, pickLabel, pickError }) {
  const pickBtn = () => onPickFromDrive ? (
    <Button variant="secondary" onClick={() => onPickFromDrive()}>
      {pickLabel || 'Choose transcript from Google Drive'}
    </Button>
  ) : null;
  const pickErr = pickError ? (
    <Banner tone="problem">{pickError}</Banner>
  ) : null;

  if (loading) {
    return (
      <div className="mtg-stack" data-testid="transcripts-loading">
        <Skeleton height={88} radius="var(--radius-inner)" label="Fetching transcripts" />
        <Skeleton height={88} radius="var(--radius-inner)" label="Fetching transcripts" />
        <p className="mtg-meta">Fetching transcripts…</p>
      </div>
    );
  }

  // A failure must never look like "nothing here": the server's message, what to do, and a retry.
  if (error) {
    return (
      <div className="mtg-stack">
        <ErrorState title="Your recordings did not load" message={error} onRetry={onLoad} retryLabel="Retry" />
        {connectNote && <p className="mtg-text">{connectNote}</p>}
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div className="mtg-stack">
        <EmptyState
          title={emptyNote}
          action={(
            <div className="mtg-actions" style={{ justifyContent: 'center' }}>
              {pickBtn()}
              <Button variant="tertiary" onClick={onLoad}>Refresh</Button>
            </div>
          )}
        >
          {emptyHint}
        </EmptyState>
        {pickErr}
      </div>
    );
  }

  return (
    <div className="mtg-stack">
      {onPickFromDrive && (
        <div className="mtg-stack" style={{ gap: 'var(--space-2)' }}>
          <div className="mtg-row mtg-row--end">{pickBtn()}</div>
          {pickErr}
        </div>
      )}
      {recordings.map(recording => (
        <RecordingCard
          key={recording[idKey]}
          recording={recording}
          idKey={idKey}
          processingId={processingId}
          results={results}
          onProcess={onProcess}
          onCreateTasks={onCreateTasks}
          onDismissResult={onDismissResult}
        />
      ))}
    </div>
  );
}

// ── Zoom tab ──────────────────────────────────────────────────────────────────
function ZoomTab() {
  const { loading, error, recordings, processingId, results, load, process, dismissResult } =
    usePlatformPanel('/api/meetings/zoom/recent-transcripts', 'meetingId');

  useEffect(() => { load(); }, [load]);

  async function createTasks(recording, items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from Zoom meeting: ${recording.meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (err) { showErrorToast(messageFromError(err, "Could not create tasks.")); }
    }
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  return (
    <PlatformTabBody
      loading={loading} error={error} recordings={recordings}
      processingId={processingId} results={results} idKey="meetingId"
      emptyNote="No Zoom cloud recordings with transcripts found in the last 7 days."
      emptyHint="Make sure cloud recording and transcription are enabled in your Zoom account settings."
      connectNote="Go to Settings, then Video meetings, to connect your Zoom account."
      onLoad={load}
      onProcess={(rec) => process(rec, '/api/meetings/zoom/process-transcript')}
      onCreateTasks={createTasks}
      onDismissResult={dismissResult}
    />
  );
}

// ── Google Meet tab ───────────────────────────────────────────────────────────
function GoogleMeetTab() {
  const { loading, error, recordings, processingId, results, load, process, dismissResult, upsertRecording } =
    usePlatformPanel('/api/meetings/google-meet/recent-transcripts', 'fileId');
  const [pickError, setPickError] = useState('');

  useEffect(() => { load(); }, [load]);
  // Warm the Google scripts when the panel opens so the pick click keeps its user-gesture (pop-up).
  useEffect(() => { preloadDrivePicker(); }, []);

  async function createTasks(recording, items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from Google Meet: ${recording.meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (err) { showErrorToast(messageFromError(err, "Could not create tasks.")); }
    }
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  // Block 178 commit 2: the Drive search is gone (it needed drive.readonly). The Google Picker
  // (drive.file) is now the ONLY way to bring in a transcript, on both the standalone "Choose from
  // Drive" action and a card's "Process" button. One path, no fallback:
  //   • recording given (a card) → Picker pre-navigated to its fileId; recording null → unnavigated.
  //   • pick → export by fileId with the drive.file token → process the text.
  // A pick failure surfaces a real message (pop-up blocked / Drive permission / generic) and resets;
  // it never hangs and never claims transcripts appear automatically (they don't anymore).
  async function pickAndProcess(recording) {
    setPickError('');
    let picked;
    try { picked = await pickGoogleDoc({ fileIds: recording?.fileId ? [recording.fileId] : undefined }); }
    catch (e) {
      setPickError(
        isPopupBlocked(e)
          ? 'Google could not open its sign-in window. Your browser may be blocking pop-ups for this site. Allow pop-ups and try again.'
        : isDriveScopeDenied(e)
          ? 'Aidey needs permission to open the file you choose from Google Drive. When Google asks, tick the Google Drive box and try again.'
          : 'Could not open Google Drive. Please try again.');
      return;
    }
    if (!picked) return; // user cancelled, quietly do nothing

    const rec = {
      fileId: picked.fileId,
      meetingTitle: recording?.meetingTitle || picked.name || 'Meeting transcript',
      date: recording?.date || '',
    };
    upsertRecording(rec);
    await process(rec, '/api/meetings/google-meet/process-transcript', async () => {
      const res = await api('/api/meetings/google-meet/export-transcript', {
        method: 'POST',
        body: JSON.stringify({ fileId: picked.fileId, accessToken: picked.accessToken }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Could not read that transcript.'); }
      const d = await res.json();
      if (!d.transcriptText) throw new Error('That document had no readable text.');
      return { transcriptText: d.transcriptText, source: 'picker' };
    });
  }

  return (
    <PlatformTabBody
      loading={loading} error={error} recordings={recordings}
      processingId={processingId} results={results} idKey="fileId"
      emptyNote="Pick a Google Meet transcript from your Google Drive to summarise it."
      emptyHint="Google Meet saves a transcript to your Drive when transcription is turned on during the call. Choose it below and Aidey will pull out the summary, decisions and action items."
      connectNote="Connect Google in Settings, then Video meetings, to reach your Meet transcripts."
      onLoad={load}
      onProcess={pickAndProcess}
      onCreateTasks={createTasks}
      onDismissResult={dismissResult}
      onPickFromDrive={() => pickAndProcess(null)}
      pickError={pickError}
      pickLabel="Choose transcript from Google Drive"
    />
  );
}

// ── Teams tab ─────────────────────────────────────────────────────────────────
function TeamsTab() {
  const { loading, error, recordings, processingId, results, load, process, dismissResult } =
    usePlatformPanel('/api/meetings/teams/recent-transcripts', 'meetingId');

  useEffect(() => { load(); }, [load]);

  async function createTasks(recording, items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from Teams meeting: ${recording.meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (err) { showErrorToast(messageFromError(err, "Could not create tasks.")); }
    }
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  const needsReconnect = error?.includes('reconnect') || error?.includes('token expired');
  const transcriptScope = error?.includes('transcript') || error?.includes('403');

  return (
    <PlatformTabBody
      loading={loading} error={error} recordings={recordings}
      processingId={processingId} results={results} idKey="meetingId"
      emptyNote="No Teams meeting transcripts found in the last 7 days."
      emptyHint="Transcripts are only available for meetings you organised. Teams must have transcription enabled in your meeting policy."
      connectNote={needsReconnect
        ? 'Reconnect Microsoft in Settings, then Video meetings, to refresh your Teams access.'
        : transcriptScope
          ? 'Transcript access may require your Teams admin to enable transcription in your meeting policy.'
          : 'Connect Microsoft in Settings, then Video meetings, to reach your Teams transcripts.'}
      onLoad={load}
      onProcess={(rec) => process(rec, '/api/meetings/teams/process-transcript')}
      onCreateTasks={createTasks}
      onDismissResult={dismissResult}
    />
  );
}

// ── Upload tab ────────────────────────────────────────────────────────────────
function UploadTab() {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [attendees, setAttendees] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(false);

  const hasContent = file || pasteText.trim().length > 10;

  function handleFileSelect(e) {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setPasteText(''); setError(null); }
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); setPasteText(''); setError(null); }
  }

  function clearFile() {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleProcess() {
    if (!hasContent) return;
    if (!meetingTitle.trim()) { setError('Please enter a meeting title.'); return; }
    setError(null);
    setProcessing(true);
    setResult(null);

    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('transcript', file);
        formData.append('meetingTitle', meetingTitle.trim());
        if (attendees.trim()) formData.append('attendees', attendees.trim());
        res = await api('/api/meetings/process-transcript', { method: 'POST', body: formData });
      } else {
        res = await api('/api/meetings/process-transcript', {
          method: 'POST',
          body: JSON.stringify({
            transcriptText: pasteText.trim(),
            meetingTitle: meetingTitle.trim(),
            attendees: attendees.trim(),
          }),
        });
      }

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Processing failed');
      }
      const data = await res.json();
      setResult(data);
      window.dispatchEvent(new Event('aidey:notesUpdated'));
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }

  async function createTasks(items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from meeting: ${meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (err) { showErrorToast(messageFromError(err, "Could not create tasks.")); }
    }
    setCreated(true);
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  function reset() {
    setFile(null);
    setPasteText('');
    setMeetingTitle('');
    setAttendees('');
    setResult(null);
    setError(null);
    setCreated(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  if (result) {
    return (
      <PostMeetingCard result={result} onCreateTasks={createTasks} onDismiss={reset} />
    );
  }

  const ready = hasContent && meetingTitle.trim();

  return (
    <div className="mtg-stack" style={{ gap: 'var(--space-4)' }}>
      <Field label="Meeting title" required>
        <Input
          value={meetingTitle}
          onChange={e => setMeetingTitle(e.target.value)}
          placeholder="e.g. Q3 planning meeting"
        />
      </Field>

      <Field label="Attendees" hint="Optional. Separate names with commas.">
        <Input
          value={attendees}
          onChange={e => setAttendees(e.target.value)}
          placeholder="e.g. Sarah, James, Marketing team"
        />
      </Field>

      {/* File drop zone */}
      <div className="ui-field">
        <span className="ui-field__label">Upload a file</span>
        {file ? (
          <div className="mtg-drop mtg-drop--file">
            <span className="mtg-drop__icon" aria-hidden="true">📄</span>
            <span className="mtg-drop__name">{file.name}</span>
            <button
              type="button"
              className="mtg__x"
              onClick={e => { e.stopPropagation(); clearFile(); }}
              aria-label={`Remove ${file.name}`}
            ><span aria-hidden="true">✕</span></button>
          </div>
        ) : (
          <button
            type="button"
            className={`mtg-drop${isDragging ? ' mtg-drop--over' : ''}`}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => !file && fileInputRef.current?.click()}
          >
            <span className="mtg-drop__icon" aria-hidden="true">📂</span>
            <span>Drop a file here or <strong className="mtg-text--ink">browse</strong></span>
            <span className="mtg-meta">.vtt, .txt or .docx, up to 10 MB</span>
          </button>
        )}
        <input ref={fileInputRef} type="file" accept=".vtt,.txt,.docx" onChange={handleFileSelect} style={{ display: 'none' }} />
      </div>

      {/* OR divider + paste area */}
      {!file && (
        <>
          <div className="mtg-or">or paste a transcript</div>

          <Field label="Transcript" hint={pasteText.trim().length > 0 ? `${pasteText.trim().split(/\s+/).length} words` : undefined}>
            <Textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder="Paste your meeting transcript here…"
              rows={7}
            />
          </Field>
        </>
      )}

      {error && <Banner tone="problem">{error}</Banner>}

      <Button
        variant="confirm"
        block
        onClick={handleProcess}
        disabled={!ready || processing}
        aria-busy={processing || undefined}
      >
        {processing ? (
          <><span className="mtg-spin mtg-spin--sm" aria-hidden="true" />Processing with Aidey…</>
        ) : 'Process with Aidey'}
      </Button>
    </div>
  );
}

// ── Main MeetingTranscripts panel ─────────────────────────────────────────────
const TABS = [
  { id: 'zoom',   label: 'Zoom',        color: 'pastel-blue' },
  { id: 'meet',   label: 'Google Meet', color: 'pastel-green' },
  { id: 'teams',  label: 'Teams',       color: 'pastel-lilac' },
  { id: 'upload', label: 'Upload',      color: 'pastel-yellow' },
];

export default function MeetingTranscripts({ onDismiss }) {
  const [activeTab, setActiveTab] = useState('zoom');

  return (
    <div className="mtg" data-testid="meeting-transcripts">
      {/* Header */}
      <header className="mtg__head">
        <div className="mtg__headtext">
          <h2 className="mtg__title">Meeting transcripts</h2>
          <p className="mtg__sub">The last 7 days, fetched and processed. Or upload any transcript.</p>
        </div>
        {onDismiss && (
          <button type="button" className="mtg__x" onClick={onDismiss} aria-label="Close meeting transcripts">
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </header>

      {/* Tab content */}
      <div className="mtg__body">
        <TabGroup tabs={TABS} value={activeTab} onChange={setActiveTab} label="Where the transcript comes from" idPrefix="mtg-tabs" />
        <TabPanel idPrefix="mtg-tabs" tabId={activeTab}>
          {activeTab === 'zoom'   && <ZoomTab />}
          {activeTab === 'meet'   && <GoogleMeetTab />}
          {activeTab === 'teams'  && <TeamsTab />}
          {activeTab === 'upload' && <UploadTab />}
        </TabPanel>
      </div>
    </div>
  );
}
